import { useMemo } from "react";

import type { PriceHistoryRow } from "../../api/articles";
import type { ProjectSummary } from "../../api/projects";
import CostCenterValue from "../../components/CostCenterValue";
import DocumentPdfActions from "../../components/DocumentPdfActions";
import HistoryExternalNumber from "../../components/HistoryExternalNumber";
import HistoryTable, { type HistoryRow } from "../../components/HistoryTable";
import { GROUP_LABELS, type GroupKey, type HistoryExportGroup } from "../../components/historyGroups";
import { PersonalViewStatus } from "../../components/PersonalViewNotice";
import { toNumber } from "../../lib/format";
import { historyCsv, historyCsvNumber } from "../../lib/historyCsv";
import { downloadOfferCsv } from "../../lib/offerCsv";
import { usePersonalView } from "../../lib/usePersonalView";
import { projectDisplay, projectSearchText } from "../../lib/projectDisplay";
import {
  costCenterTypeFromProjectKey,
  costCenterTypeLabel,
} from "../../lib/costCenterDisplay";
import { articleDisplayPrices } from "./ArticlePriceSummary";

const COLUMNS = ["date", "document", "costCenter", "supplier", "qty", "unitPrice", "deviation"] as const;
const PAGE_SIZE = 10;

/**
 * Die Belege zum Verlauf nutzen dieselbe Historientabelle wie die
 * Artikelhistorie (#544): gleicher Aufbau, gleiche Sortierung, gleiche
 * Kartenansicht auf dem Telefon. Nur der Spaltenumfang ist kompakter.
 */
export default function ArticleHistoryList({
  rows,
  projectByKey,
}: {
  rows: PriceHistoryRow[];
  projectByKey?: ReadonlyMap<string, ProjectSummary>;
}) {
  const personal = usePersonalView("article_history", { restoreToUrl: false });
  const groupValue = personal.params.get("history_group") ?? "none";
  const groupBy = Object.hasOwn(GROUP_LABELS, groupValue) ? groupValue as GroupKey : null;
  const mergeEqual = personal.params.get("history_merge") === "1";
  const remembered = personal.saved && personal.savedParams?.history_group === (groupBy ?? "none")
    && personal.savedParams?.history_merge === (mergeEqual ? "1" : "0");
  const { kpis, comparable } = articleDisplayPrices(rows);
  // Highest comparable price in the period: red, like the lowest is green.
  const worstPrice = useMemo(() => {
    if (!comparable || kpis.bestPrice === null) return null;
    const prices = rows.filter(isPriceComparable).map((row) => toNumber(row.net_unit_price) ?? 0);
    const max = prices.length ? Math.max(...prices) : null;
    return max !== null && max > kpis.bestPrice ? max : null;
  }, [comparable, kpis.bestPrice, rows]);
  const historyRows = useMemo<HistoryRow<PriceHistoryRow>[]>(
    () =>
      rows.map((row, index) => {
        const isBest = kpis.bestPrice !== null && toNumber(row.net_unit_price) === kpis.bestPrice && isPriceComparable(row);
        const isWorst = worstPrice !== null && toNumber(row.net_unit_price) === worstPrice && isPriceComparable(row);
        return {
          id: `${row.invoice_doc_id ?? row.doc_number ?? "doc"}-${row.line_pos ?? index}`,
          data: row,
          date: row.doc_date,
          document: <div>{row.invoice_doc_id ? (
            <DocumentPdfActions
              docId={row.invoice_doc_id}
              docNumber={row.doc_number}
              linePos={row.line_pos}
              pageNo={row.page_no}
            />
          ) : (
            <span className="font-mono text-xs text-slate-600">{row.doc_number ?? "–"}</span>
          )}<HistoryExternalNumber value={row.external_number} /></div>,
          costCenter: (
            <CostCenterValue
              projectKey={row.project_key}
              deliveryAddressDisplay={row.delivery_address_display}
              projectByKey={projectByKey}
            />
          ),
          supplier: row.supplier ?? "–",
          qty: row.qty,
          uom: row.uom,
          unitPrice: row.net_unit_price,
          deviation: priceDeviation(row),
          total: row.line_net_amount,
          priceComparable: row.doc_type === "rechnung" && !row.source_kind?.startsWith("ugl") && (toNumber(row.qty) ?? 0) > 0,
          comparisonKey: "current-article",
          mergeKey: JSON.stringify([row.source_kind, row.doc_type]),
          groupLabels: { costCenter: costCenterLabel(row, projectByKey), supplier: row.supplier, document: row.doc_number ?? row.invoice_doc_id },
          groupIds: { costCenter: row.project_key ?? "unassigned", document: row.invoice_doc_id ?? row.doc_number ?? "unknown" },
          unitPriceNote: isBest ? (
            <span className="text-xs text-emerald-800">Niedrigster Preis</span>
          ) : isWorst ? (
            <span className="text-xs text-rose-700">Höchster Preis</span>
          ) : null,
          highlight: isBest ? "best" : isWorst ? "worst" : undefined,
          searchText: [
            row.doc_number,
            row.external_number,
            row.supplier,
            costCenterText(row, projectByKey),
            row.description,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase(),
        };
      }),
    [rows, projectByKey, kpis.bestPrice],
  );

  return (
    <section aria-label="Die Belege zum Verlauf" className="min-w-0">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">Die Belege zum Verlauf</h2>
        <p className="text-xs text-slate-600">
          {rows.length} {rows.length === 1 ? "Position" : "Positionen"} im Zeitraum
        </p>
      </div>
      <HistoryTable
        rows={historyRows}
        columns={COLUMNS}
        groupable
        grouping={{ groupBy, mergeEqual, onChange: (nextGroup, nextMerge) => {
          const next = new URLSearchParams(personal.params);
          next.set("history_group", nextGroup ?? "none"); next.set("history_merge", nextMerge ? "1" : "0");
          personal.setUrl(next, { replace: true });
        } }}
        rememberGrouping={<div className="flex flex-wrap items-center gap-3">
          <PersonalViewStatus view={personal} />
          <button type="button" disabled={!personal.ready || personal.pending} className="min-h-9 text-emerald-800 disabled:opacity-50" onClick={() => {
            const next = new URLSearchParams(personal.params);
            next.set("history_group", groupBy ?? "none"); next.set("history_merge", mergeEqual ? "1" : "0");
            personal.change(next, { replace: true });
          }}>{personal.pending ? "Wird gemerkt …" : remembered ? "Gruppierung gemerkt" : "Diese Gruppierung merken"}</button>
        </div>}
        onExport={(exportRows, groups) => downloadOfferCsv(articleHistoryCsv(exportRows, groups, projectByKey), "artikel-belege.csv")}
        pageSize={PAGE_SIZE}
        testId="article-history-table"
        minWidthClass="min-w-[640px]"
        searchPlaceholder="Beleg, Kostenstelle, Großhändler …"
        emptyText="Keine Belege in diesem Zeitraum."
      />
    </section>
  );
}

export function articleHistoryCsv(
  rows: readonly PriceHistoryRow[],
  groups: readonly HistoryExportGroup<PriceHistoryRow>[] | undefined,
  projectByKey?: ReadonlyMap<string, ProjectSummary>,
): string {
  const header = ["Datum", "Beleg", "ER-Nummer", "Kostenstelle", "Großhändler", "Menge", "Einheit", "Netto-Preis", "Abweichung %"];
  const position = (row: PriceHistoryRow): unknown[] => [
    row.doc_date, row.doc_number, row.external_number, costCenterLabel(row, projectByKey), row.supplier,
    historyCsvNumber(row.qty), row.uom, historyCsvNumber(row.net_unit_price),
    historyCsvNumber(row.canonical_delta_pct ?? null),
  ];
  if (!groups) return historyCsv([header, ...rows.map(position)]);
  const groupColumns = ["Gruppenzeile", "Gruppenname", "Gruppenpositionen", "Gruppenmenge", "Gruppeneinheit", "Gruppensumme", "Gruppen-Ø-Preis", "Gruppen-Bestpreis"];
  const blank = () => Array<string>(header.length).fill("");
  return historyCsv([[...header, ...groupColumns], ...groups.flatMap(group => [
    [...blank(), "Gruppe", group.label, "", "", "", "", "", ""],
    ...group.rows.map(row => [...position(row), ...Array<string>(groupColumns.length).fill("")]),
    [...blank(), "Gruppensumme", group.label, group.summary.count, group.summary.qty,
      group.summary.uom, group.summary.total, group.summary.average, group.summary.best],
  ])]);
}

/**
 * Abweichung zum Vergleichspreis der Rechnungsposition, je Einheit und in
 * Prozent – derselbe Wert wie in den Belegdetails. Ohne früheren Preis keiner.
 */
function priceDeviation(row: PriceHistoryRow): { amount: number | null; percent: string | number } | null {
  const percent = row.canonical_delta_pct;
  if (percent == null || toNumber(percent) === null) return null;
  // Same price the deviation is measured on: after credits, else the invoice price.
  const paid = toNumber(row.effective_unit_price ?? row.net_unit_price);
  const baseline = toNumber(row.canonical_baseline_price);
  return { amount: paid !== null && baseline !== null ? paid - baseline : null, percent };
}

/** Invoice positions with a quantity count for lowest and highest price; UGL data does not. */
function isPriceComparable(row: PriceHistoryRow): boolean {
  return row.doc_type === "rechnung" && !row.source_kind?.startsWith("ugl") && (toNumber(row.qty) ?? 0) > 0 && (toNumber(row.net_unit_price) ?? 0) > 0;
}

function costCenterText(
  row: PriceHistoryRow,
  projectByKey?: ReadonlyMap<string, ProjectSummary>,
): string {
  const project = row.project_key ? projectByKey?.get(row.project_key) : undefined;
  const type = row.project_key ? costCenterTypeFromProjectKey(row.project_key) : null;
  return [
    type ? costCenterTypeLabel(type) : null,
    projectSearchText(project, row.project_key),
    row.delivery_address_display,
  ]
    .filter(Boolean)
    .join(" ");
}

function costCenterLabel(
  row: PriceHistoryRow,
  projectByKey?: ReadonlyMap<string, ProjectSummary>,
): string {
  if (!row.project_key) return "Nicht zugeordnet";
  const type = costCenterTypeFromProjectKey(row.project_key);
  const display = projectDisplay(projectByKey?.get(row.project_key), row.project_key);
  const title = (type === "delivery_address" && row.delivery_address_display) || display?.title;
  if (!title) return "Nicht zugeordnet";
  const prefix = title.startsWith(`${costCenterTypeLabel(type)} `) ? "" : `${costCenterTypeLabel(type)} `;
  return `${prefix}${title}${display?.meta ? ` · ${display.meta}` : ""}`;
}
